import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, RouterOutlet } from '@angular/router';
import { BaseDirective } from '@shared/directives/base/base.directive';
import { LoaderComponent, ToastComponent } from 'cve-ui';
import { Banner, ImpersonationFacade } from 'nextmove-common-impersonation';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { BreakpointService } from './infrastructure/services/breakpoint.service';
import { GoogleAnalyticService } from './infrastructure/services/google-analytic.service';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, LoaderComponent, ToastComponent, Banner],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent extends BaseDirective implements OnInit {
    breakpointService = inject(BreakpointService);
    googleAnalyticService = inject(GoogleAnalyticService);
    readonly impersonationService = inject(ImpersonationFacade);
    readonly isImpersonationErrorRoute = signal(false);

    constructor() {
        super();
        this.breakpointService.set();
        this.loadGTM();

        this.updateImpersonationErrorRoute(this.router.url);
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => {
                this.updateImpersonationErrorRoute(event.urlAfterRedirects);
            });

        // Redirect when a new session replaces current one in another tab
        toObservable(this.impersonationService.newSessionDetected)
            .pipe(
                filter((detected: boolean) => detected),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.router.navigate(['/impersonation-error'], { queryParams: { reason: 'expired' } });
            });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.checkImpersonationCode();
    }

    private updateImpersonationErrorRoute(url: string): void {
        const path = url.split(/[?#]/, 1)[0].replace(/\/$/, '');
        this.isImpersonationErrorRoute.set(path === '/impersonation-error');
    }

    private checkImpersonationCode(): void {
        const code = this.impersonationService.getCodeFromUrl();

        if (code && !this.impersonationService.hasCodeBeenRedeemed(code)) {
            this.impersonationService
                .redeemCode(code)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.impersonationService.cleanCodeFromUrl();
                        this.router.navigate(['/vehicles']);
                    },
                    error: (err: HttpErrorResponse) => {
                        this.impersonationService.cleanCodeFromUrl();
                        if (err.status === 429) {
                            this.router.navigate(['/impersonation-error'], { queryParams: { reason: 'rate_limit' } });
                        } else {
                            this.impersonationService.clearSession();
                            this.router.navigate(['/login']);
                        }
                    },
                });
        } else if (this.impersonationService.isActive()) {
            const path = window.location.pathname;
            if (path === '/' || path.includes('login') || path.includes('registration')) {
                this.router.navigate(['/vehicles']);
            }
        }
    }

    reloadPage(): void {
        window.location.reload();
    }

    closeTab(): void {
        window.close();
    }

    private loadGTM(): void {
        if (!environment.google_tag_manager) {
            return; // GTM disabled in this environment
        }

        const gtmId = environment.google_tag_manager;

        const gtmAuthParams = environment.gtm_auth
            ? `&gtm_auth=${environment.gtm_auth}&gtm_preview=${environment.gtm_preview}&gtm_cookies_win=${environment.gtm_cookies_win}`
            : '';

        const gtmScript = document.createElement('script');
        gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl+'${gtmAuthParams}';f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;

        if (document.head.firstChild) {
            document.head.insertBefore(gtmScript, document.head.firstChild);
        } else {
            document.head.appendChild(gtmScript);
        }

        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}${gtmAuthParams}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);

        if (document.body.firstChild) {
            document.body.insertBefore(noscript, document.body.firstChild);
        } else {
            document.body.appendChild(noscript);
        }
    }
}
